import React, { useState, useEffect, useRef, createRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  IconButton,
  Skeleton,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EnhancedChart from '../components/common/EnhancedChart';
import ChartContainer from '../components/common/ChartContainer';
import { dataTypeColors } from '../utils/colorUtils';

const CustomDashboard = () => {
  const theme = useTheme();
  const [pinnedCharts, setPinnedCharts] = useState([]);
  const [chartData, setChartData] = useState({});
  const [chartRefs, setChartRefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  useEffect(() => {
    fetchPinnedCharts();
  }, []);
  
  // Create refs for each chart
  useEffect(() => {
    const refs = {};
    pinnedCharts.forEach(chart => {
      refs[chart._id] = createRef();
    });
    setChartRefs(refs);
  }, [pinnedCharts]);
  
  // Fetch all pinned charts
  const fetchPinnedCharts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/dashboard/pinned');
      setPinnedCharts(response.data);

      // For each pinned chart, fetch the actual chart data
      const chartDataPromises = response.data.map(chart => fetchChartData(chart));
      const chartDataResults = await Promise.allSettled(chartDataPromises);
      
      const newChartData = {};
      chartDataResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          newChartData[response.data[index]._id] = result.value;
        }
      });
      
      setChartData(newChartData);
      setError(null);
    } catch (err) {
      console.error('Error fetching pinned charts:', err);
      setError('Failed to load pinned charts');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch actual chart data for a specific chart
  const fetchChartData = async (chart) => {
    try {
      // If we have a dataEndpoint, use it
      const endpoint = chart.dataEndpoint || determineEndpointFromChartId(chart.chartId);
      
      // If we have stored chartData and it's not empty, use it instead of fetching
      if (chart.chartData && Object.keys(chart.chartData).length > 0) {
        return chart.chartData;
      }
      
      const response = await axios.get(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Error fetching data for chart ${chart.chartId}:`, error);
      
      // Return dummy data to prevent infinite loading
      return generateFallbackChartData(chart.chartId, chart.chartType);
    }
  };
  
  // Determine API endpoint based on chart ID
  const determineEndpointFromChartId = (chartId) => {
    const parts = chartId.split('-');
    
    // Use more specific endpoints that actually exist in the backend
    if (parts.includes('ev')) {
      return '/api/vehicles/ev-analytics';
    } else if (parts.includes('ice')) {
      return '/api/vehicles/ice-analytics';
    } else if (parts.includes('comparison')) {
      return '/api/vehicles/comparison';
    } else if (parts.includes('sales')) {
      return '/api/vehicles/sales';
    }
    
    // Default fallback - use a known working endpoint
    return '/api/vehicles/ev-analytics';
  };
  
  // Generate fallback chart data when API fails
  const generateFallbackChartData = (chartId, chartType) => {
    if (chartId.includes('ev')) {
      return {
        monthlyData: [
          { month: 'Jan', sales: 1200 },
          { month: 'Feb', sales: 1300 },
          { month: 'Mar', sales: 1400 },
          { month: 'Apr', sales: 1600 },
          { month: 'May', sales: 1800 }
        ]
      };
    } else if (chartId.includes('ice')) {
      return {
        monthlyData: [
          { month: 'Jan', sales: 2200 },
          { month: 'Feb', sales: 2100 },
          { month: 'Mar', sales: 2000 },
          { month: 'Apr', sales: 1900 },
          { month: 'May', sales: 1850 }
        ]
      };
    } else if (chartId.includes('comparison')) {
      return [
        { maker: 'Tesla', totalSales: { ev: 5000, ice: 0 } },
        { maker: 'Toyota', totalSales: { ev: 1500, ice: 8000 } },
        { maker: 'Ford', totalSales: { ev: 2000, ice: 6000 } }
      ];
    }
    
    // Default fallback data
    return {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
      datasets: [
        {
          label: 'Sample Data',
          data: [1000, 1200, 1500, 1300, 1700]
        }
      ]
    };
  };
  
  // Handle chart deletion
  const handleDeleteChart = async (id) => {
    try {
      await axios.delete(`/api/dashboard/pin/${id}`);
      setPinnedCharts(prevCharts => prevCharts.filter(chart => chart._id !== id));
      setSnackbar({
        open: true,
        message: 'Chart removed from dashboard',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error removing chart:', err);
      setSnackbar({
        open: true,
        message: 'Failed to remove chart',
        severity: 'error'
      });
    }
  };
  
  // Prepare chart data for rendering: pinned charts store Chart.js { labels, datasets } from the pin action.
  const prepareChartData = (chart, data) => {
    if (!data) return null;

    // Snapshot saved at pin time — use as-is (do not treat dashboard-* ids as "ev monthly" API shape).
    if (Array.isArray(data.labels) && Array.isArray(data.datasets)) {
      return {
        labels: [...data.labels],
        datasets: data.datasets.map((ds) => ({ ...ds })),
      };
    }

    // Legacy: refetched API payloads (ev/ice analytics)
    if (chart.chartId.includes('ev') && data.monthlyData) {
      return {
        labels: data.monthlyData.map((item) => item.month),
        datasets: [
          {
            label: 'Total EV Sales',
            data: data.monthlyData.map((item) => item.sales),
            fill: true,
          },
        ],
      };
    }
    if (chart.chartId.includes('ice') && data.monthlyData) {
      return {
        labels: data.monthlyData.map((item) => item.month),
        datasets: [
          {
            label: 'Total ICE Sales',
            data: data.monthlyData.map((item) => item.sales),
            fill: true,
          },
        ],
      };
    }
    if (chart.chartId.includes('comparison') && Array.isArray(data)) {
      return {
        labels: data.map((item) => item.maker),
        datasets: [
          {
            label: 'Total EV Sales',
            data: data.map((item) => item.totalSales?.ev),
            backgroundColor: dataTypeColors.ev.primary,
            borderColor: dataTypeColors.ev.primary,
          },
          {
            label: 'Total ICE Sales',
            data: data.map((item) => item.totalSales?.ice),
            backgroundColor: dataTypeColors.ice.primary,
            borderColor: dataTypeColors.ice.primary,
          },
          {
            label: 'Total Sales',
            data: data.map((item) => {
              const ev = item.totalSales?.ev;
              const ice = item.totalSales?.ice;
              if (ev !== undefined && ice !== undefined) return ev + ice;
              return 0;
            }),
            backgroundColor: dataTypeColors.combined.primary,
            borderColor: dataTypeColors.combined.primary,
          },
        ],
      };
    }

    return {
      labels: ['No Data'],
      datasets: [{ label: 'No Data', data: [0] }],
    };
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      pl: 0,
      ml: 0
    }}>
      <Box sx={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 10,
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Typography variant="h5" sx={{ px: 2, pt: 2, pb: 1 }}>
          Custom Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
          Your pinned charts as live visualizations
        </Typography>
      </Box>
      
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        px: 2,
        pt: 2,
        pb: 4,
        '::-webkit-scrollbar': {
          width: '8px'
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.primary.main, 0.1),
          borderRadius: '4px'
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: alpha(theme.palette.primary.main, 0.2)
        }
      }}>
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} md={6} key={item}>
                <Card>
                  <Skeleton variant="rectangular" height={250} />
                  <CardContent>
                    <Skeleton width="80%" />
                    <Skeleton width="40%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : pinnedCharts.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              py: 8,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No pinned charts yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pin charts from the analytics pages to see them here
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {pinnedCharts.map((chart) => {
              const data = chartData[chart._id];
              const preparedData = prepareChartData(chart, data);
              
              return (
                <Grid item xs={12} sm={6} md={6} key={chart._id}>
                  <ChartContainer
                    title={chart.title}
                    isLoading={!data}
                    height="250px"
                    chartRef={!loading && chartRefs[chart._id]}
                    chartId={chart.chartId}
                    chartType={chart.chartType}
                    action={
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteChart(chart._id)}
                        aria-label="delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    {preparedData && (
                      <EnhancedChart
                        ref={chartRefs[chart._id]}
                        type={chart.chartType}
                        data={preparedData}
                        options={{
                          scales: {
                            y: {
                              beginAtZero: true
                            }
                          }
                        }}
                        colorPalette={
                          chart.chartId.includes('ev') ? 'ev' :
                          chart.chartId.includes('ice') ? 'ice' : 'combined'
                        }
                      />
                    )}
                  </ChartContainer>
                </Grid>
              );
            })}
          </Grid>
        )}

      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomDashboard; 